import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Search, Trash2, X } from "lucide-react";
import { ApiError } from "@/api/client";
import { useCreateScenario, useDeleteScenario, useScenarios } from "@/api/scenarios";
import type { ScenarioSummary } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MultiSelectFilter,
  type MultiSelectOption,
} from "@/components/ui/multi-select-filter";
import { cn } from "@/lib/utils";
import {
  matchesQuery,
  presentIndustries,
  presentStacks,
  scenarioFailureKind,
  scenarioIndustry,
  stackLabel,
} from "./scenarioCategories";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Lift the bg-{color} token out of the chip className so we can re-use it
 * as a 1.5px dot in the dropdown list. Looks for a class like "bg-blue-500/15"
 * and strips the opacity suffix so the dot renders solidly.
 */
function chipDotClass(chipClass: string): string {
  const match = chipClass.match(/bg-[a-z]+-\d{2,3}(?:\/\d+)?/);
  if (!match) return "bg-muted-foreground";
  return match[0].replace(/\/\d+$/, "");
}

const SKELETON_YAML = `\
scenario:
  name: new-scenario
  description: Describe me
  root_cause: x
  window:
    start: "2026-01-01T00:00:00"
    duration: 2h
topology:
  services: []
timeline: []
`;

export function ScenariosListPage() {
  const navigate = useNavigate();
  const scenarios = useScenarios();
  const create = useCreateScenario();
  const del = useDeleteScenario();
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [industries, setIndustries] = useState<Set<string>>(new Set());
  const [stacks, setStacks] = useState<Set<string>>(new Set());
  const [clouds, setClouds] = useState<Set<string>>(new Set());
  const permissions = usePermissions();

  const items = scenarios.data ?? [];
  const industryDefs = useMemo(() => presentIndustries(items), [items]);
  const stackIds = useMemo(() => presentStacks(items), [items]);

  const industryOptions: MultiSelectOption[] = useMemo(
    () =>
      industryDefs.map((i) => ({
        value: i.id,
        label: i.label,
        dotClass: chipDotClass(i.className),
      })),
    [industryDefs],
  );
  const stackOptions: MultiSelectOption[] = useMemo(
    () => stackIds.map((id) => ({ value: id, label: stackLabel(id), mono: true })),
    [stackIds],
  );
  /**
   * Only the clouds actually present, so the filter never offers an option
   * that would empty the list. "agnostic" is a real bucket, not a missing
   * value: those scenarios run on any backend and are the ones an SE wants
   * when the target backend's cloud is not the scenario's.
   */
  const cloudOptions: MultiSelectOption[] = useMemo(() => {
    const present = new Set(items.map((s) => s.cloud ?? "agnostic"));
    return ["aws", "azure", "gcp", "agnostic"]
      .filter((c) => present.has(c))
      .map((c) => ({
        value: c,
        label: c === "agnostic" ? "Agnostic" : c.toUpperCase(),
      }));
  }, [items]);

  const filtered = useMemo(
    () =>
      items.filter((s) => {
        if (!matchesQuery(s, query)) return false;
        if (industries.size > 0 && !industries.has(scenarioIndustry(s).id)) return false;
        if (stacks.size > 0) {
          const scenarioStacks = (s.stacks ?? []).map((t) => t.toLowerCase());
          if (!scenarioStacks.some((t) => stacks.has(t))) return false;
        }
        // "agnostic" covers scenarios with no declared cloud — they run
        // anywhere, so they match whichever cloud filter is applied.
        if (clouds.size > 0 && !clouds.has(s.cloud ?? "agnostic")) return false;
        return true;
      }),
    [items, query, industries, stacks, clouds],
  );

  const filtersActive = !!(query || industries.size || stacks.size || clouds.size);

  async function onCreate() {
    if (!permissions.canMutate) return;
    setError(null);
    try {
      const result = await create.mutateAsync(SKELETON_YAML);
      navigate(`/scenarios/${result.slug}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Scenarios</h2>
          <p className="text-sm text-muted-foreground">
            Pick a scenario to inspect, edit, or start a run against a backend.
          </p>
        </div>
        <Button
          onClick={onCreate}
          disabled={!permissions.canMutate || create.isPending}
          title={!permissions.canMutate ? (permissions.mutationReason ?? "Insufficient permission") : undefined}
        >
          <Plus size={14} /> {create.isPending ? "Creating…" : "New scenario"}
        </Button>
      </header>

      {!permissions.canMutate && (
        <p className="text-xs text-muted-foreground">
          {permissions.mutationReason ?? "Insufficient permissions for this action."}
        </p>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Toolbar
        query={query}
        setQuery={setQuery}
        industryOptions={industryOptions}
        industries={industries}
        setIndustries={setIndustries}
        stackOptions={stackOptions}
        stacks={stacks}
        setStacks={setStacks}
        cloudOptions={cloudOptions}
        clouds={clouds}
        setClouds={setClouds}
        total={items.length}
        shown={filtered.length}
        filtersActive={filtersActive}
      />

      {filtered.length === 0 ? (
        <EmptyState
          hasItems={items.length > 0}
          onCreate={onCreate}
          isLoading={scenarios.isLoading}
          canMutate={permissions.canMutate}
          mutationReason={permissions.mutationReason}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((s) => (
            <ScenarioCard
              key={s.slug}
              scenario={s}
              onOpen={() => navigate(`/scenarios/${s.slug}`)}
              canMutate={permissions.canMutate}
              mutationReason={permissions.mutationReason}
              onDelete={() => {
                if (confirm(`Delete ${s.slug}?`)) del.mutate(s.slug);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ToolbarProps {
  query: string;
  setQuery: (v: string) => void;
  industryOptions: MultiSelectOption[];
  industries: Set<string>;
  setIndustries: (next: Set<string>) => void;
  stackOptions: MultiSelectOption[];
  stacks: Set<string>;
  setStacks: (next: Set<string>) => void;
  cloudOptions: MultiSelectOption[];
  clouds: Set<string>;
  setClouds: (next: Set<string>) => void;
  total: number;
  shown: number;
  filtersActive: boolean;
}

function Toolbar({
  query,
  setQuery,
  industryOptions,
  industries,
  setIndustries,
  stackOptions,
  stacks,
  setStacks,
  cloudOptions,
  clouds,
  setClouds,
  total,
  shown,
  filtersActive,
}: ToolbarProps) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[16rem] max-w-md">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <Input
          placeholder="Search by name, slug, description, or stack…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 h-9"
        />
      </div>
      <MultiSelectFilter
        label="Industry"
        options={industryOptions}
        selected={industries}
        onChange={setIndustries}
        searchPlaceholder="Search industries…"
      />
      <MultiSelectFilter
        label="Stack"
        options={stackOptions}
        selected={stacks}
        onChange={setStacks}
        searchPlaceholder="Search stacks…"
      />
      {cloudOptions.length > 1 && (
        <MultiSelectFilter
          label="Cloud"
          options={cloudOptions}
          selected={clouds}
          onChange={setClouds}
          searchPlaceholder="Search clouds…"
        />
      )}
      {filtersActive && (
        <>
          <span className="text-xs text-muted-foreground">
            {shown} of {total}
          </span>
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setIndustries(new Set());
              setStacks(new Set());
              setClouds(new Set());
            }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={12} /> Clear
          </button>
        </>
      )}
    </div>
  );
}

function ScenarioCard({
  scenario,
  onOpen,
  onDelete,
  canMutate,
  mutationReason,
}: {
  scenario: ScenarioSummary;
  onOpen: () => void;
  onDelete: () => void;
  canMutate: boolean;
  mutationReason: string | null;
}) {
  const industry = scenarioIndustry(scenario);
  const failure = scenarioFailureKind(scenario);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-foreground/30 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              "inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border uppercase tracking-wide",
              industry.className,
            )}
          >
            {industry.label}
          </span>
          {failure && (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border border-border text-muted-foreground uppercase tracking-wide">
              {failure}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={!canMutate}
          title={mutationReason ?? "Insufficient permission"}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive disabled:opacity-30"
          aria-label={`Delete ${scenario.slug}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
      <h3 className="text-sm font-semibold text-foreground font-mono line-clamp-1">
        {scenario.slug}
      </h3>
      {scenario.name && scenario.name !== scenario.slug && (
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{scenario.name}</p>
      )}
      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{scenario.description}</p>
      {scenario.cloud && (
        <div className="flex items-center gap-1 flex-wrap mt-3">
          <span
            className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-semibold uppercase tracking-wide border border-sky-500/50 text-sky-500"
            title={`Written against ${scenario.cloud} resources — it can only run on a ${scenario.cloud} backend or a vendor-neutral one`}
          >
            {scenario.cloud}
          </span>
        </div>
      )}
      {scenario.stacks && scenario.stacks.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mt-3">
          {scenario.stacks.slice(0, 5).map((s) => (
            <span
              key={s}
              className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono bg-muted text-muted-foreground"
            >
              {s}
            </span>
          ))}
          {scenario.stacks.length > 5 && (
            <span className="text-[10px] text-muted-foreground">
              +{scenario.stacks.length - 5}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-end mt-3 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
        Open <ChevronRight size={12} className="ml-0.5" />
      </div>
    </div>
  );
}

function EmptyState({
  hasItems,
  onCreate,
  isLoading,
  canMutate,
  mutationReason,
}: {
  hasItems: boolean;
  onCreate: () => void;
  isLoading: boolean;
  canMutate: boolean;
  mutationReason: string | null;
}) {
  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>;
  }
  return (
    <div className="border border-dashed border-border rounded-lg py-12 text-center space-y-3">
      <p className="text-sm text-muted-foreground">
        {hasItems ? "No scenarios match the current filter." : "No scenarios yet."}
      </p>
      {!hasItems && (
        <Button
          onClick={onCreate}
          disabled={!canMutate}
          title={mutationReason ?? "Insufficient permission"}
        >
          <Plus size={14} /> New scenario
        </Button>
      )}
    </div>
  );
}
